import React, { useCallback, useMemo } from 'react';

import dayjs from '~/timeInit';

import { Slider, Stack, Typography } from '@mui/material';

import { TimelinePoint } from './departureInventory';

interface Props {
  timeline: Array<TimelinePoint>;
  date?: number;
  onDateChange: (date: number) => void;
}

export default function InventoryDateSlider({ timeline, date, onDateChange }: Props) {
  const range = useMemo(() => {
    if (!timeline.length) {
      return undefined;
    }
    const times = timeline.map((point) => dayjs(point.occurredAt).valueOf());
    const start = dayjs(Math.min(...times))
      .startOf('day')
      .subtract(1, 'day');
    const end = dayjs(Math.max(...times)).startOf('day');
    return { start, days: end.diff(start, 'day') };
  }, [timeline]);
  const formatDay = useCallback(
    (offset: number) => range?.start.add(offset, 'day').format('D MMM YYYY') ?? '',
    [range],
  );
  const onChange = useCallback(
    (_event: Event, value: number | Array<number>) => {
      if (range && typeof value === 'number') {
        onDateChange(range.start.add(value, 'day').valueOf());
      }
    },
    [range, onDateChange],
  );

  if (!range) {
    return null;
  }
  const value =
    date === undefined ? range.days : Math.max(0, Math.min(range.days, dayjs(date).diff(range.start, 'day')));

  return (
    <Stack spacing={1} sx={{ px: 2 }}>
      <Typography variant="body2">Inventory snapshot: {formatDay(value)} (end of day)</Typography>
      <Slider
        aria-label="Inventory snapshot date"
        min={0}
        max={range.days}
        step={1}
        value={value}
        onChange={onChange}
        getAriaValueText={formatDay}
        valueLabelFormat={formatDay}
        valueLabelDisplay="auto"
      />
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          {formatDay(0)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDay(range.days)}
        </Typography>
      </Stack>
    </Stack>
  );
}
